import { useEffect, useRef, useState } from "react";

import { AssetHotspots } from "@/components/asset-hotspots";
import type {
  AssetResource,
  ImageAssetResource,
  VideoAssetVariantSourceUrls,
  VideoAssetResource,
} from "@/hook/use_app_assets";
import { use_asset_view_settle } from "@/hook/use_asset_view_settle";
import { use_fallback_resource_source } from "@/hook/use_fallback_resource_source";
import { use_snapped_video_container_style } from "@/hook/use_snapped_video_container_style";
import { get_asset_preview_element_id } from "@/lib/asset-preview-id";
import type { AssetIndexBySequenceNumber } from "@/lib/portfolio-navigation";
import { cn } from "@/lib/utils";

type AssetPreviewProps = {
  asset_index_by_sequence_number: AssetIndexBySequenceNumber;
  asset_index: number;
  asset_resource: AssetResource;
  on_asset_view_settle: (asset_index: number) => void;
  should_use_original_image_mode: boolean;
};

type ImageAssetPreviewProps = {
  asset_resource: ImageAssetResource;
  should_load_resource: boolean;
  should_use_original_image_mode: boolean;
};

type VideoAssetPreviewProps = {
  asset_resource: VideoAssetResource;
  should_load_resource: boolean;
};

type UseLazyResourceLoadingParams = {
  load_trigger_delay_ms: number;
  root_margin: string;
  should_load_immediately: boolean;
};

const ASSET_PREVIEW_CONTAINER_CLASS_NAME =
  "relative aspect-video w-full bg-[#ECE0B9] md:mx-auto md:w-[min(63.333333vw,1216px)] md:bg-transparent";
const ASSET_PREVIEW_MEDIA_CLASS_NAME = "block h-full w-full object-contain";
const ASSET_PREVIEW_LAYER_CLASS_NAME =
  "absolute inset-0 block h-full w-full object-contain";
const VIDEO_ASSET_FRAME_CLASS_NAME = "absolute inset-0 overflow-hidden";
const VIDEO_ASSET_PREVIEW_MEDIA_CLASS_NAME =
  "mx-auto block h-full w-full object-contain max-md:w-auto max-md:max-w-none";
const FINAL_IMAGE_CLASS_NAME =
  "absolute inset-0 block h-full w-full object-contain opacity-0 transition-opacity duration-300";
const FIRST_EAGER_ASSET_COUNT = 1;
const IMAGE_FALLBACK_TIMEOUT_MS = 3000;
const IMAGE_LOAD_SETTLE_DELAY_MS = 0;
const IMAGE_PRELOAD_ROOT_MARGIN = "2400px 0px";
const VIDEO_FALLBACK_TIMEOUT_MS = 5000;
const VIDEO_LOAD_SETTLE_DELAY_MS = 0;
const MOBILE_VIDEO_ASSET_MEDIA_QUERY = "(max-width: 767px)";
const VIDEO_PRELOAD_ROOT_MARGIN = "600px 0px";

export function AssetPreview({
  asset_index_by_sequence_number,
  asset_index,
  asset_resource,
  on_asset_view_settle,
  should_use_original_image_mode,
}: AssetPreviewProps) {
  const should_load_immediately = asset_index < FIRST_EAGER_ASSET_COUNT;
  const resource_loading_options =
    get_resource_lazy_loading_options(asset_resource);
  const { resource_container_ref, should_load_resource } =
    use_lazy_resource_loading({
      ...resource_loading_options,
      should_load_immediately,
    });
  const asset_preview_container_style = use_snapped_video_container_style({
    asset_container_ref: resource_container_ref,
    should_snap_height: asset_resource.kind === "video",
  });
  const asset_preview_container_class_name =
    get_asset_preview_container_class_name();

  use_asset_view_settle({
    asset_container_ref: resource_container_ref,
    asset_index,
    on_asset_view_settle,
  });

  return (
    <div
      id={get_asset_preview_element_id(asset_index)}
      ref={resource_container_ref}
      className={asset_preview_container_class_name}
      style={asset_preview_container_style}
    >
      {asset_resource.kind === "video" ? (
        <VideoAssetPreview
          asset_resource={asset_resource}
          should_load_resource={should_load_resource}
        />
      ) : (
        <ImageAssetPreview
          asset_resource={asset_resource}
          should_load_resource={should_load_resource}
          should_use_original_image_mode={should_use_original_image_mode}
        />
      )}
      <AssetHotspots
        asset_index_by_sequence_number={asset_index_by_sequence_number}
        asset_resource={asset_resource}
      />
    </div>
  );
}

function VideoAssetPreview({
  asset_resource,
  should_load_resource,
}: VideoAssetPreviewProps) {
  const should_use_mobile_video_variant = use_should_use_mobile_video_variant();
  const video_asset_playback_source_urls = get_video_asset_playback_source_urls({
    asset_resource,
    should_use_mobile_video_variant,
  });
  const video_source = use_fallback_resource_source({
    fallback_source_url: video_asset_playback_source_urls.fallback_source_url,
    primary_source_url: video_asset_playback_source_urls.source_url,
    should_load_source: should_load_resource,
    timeout_ms: VIDEO_FALLBACK_TIMEOUT_MS,
  });

  return (
    <div className={VIDEO_ASSET_FRAME_CLASS_NAME}>
      <video
        aria-label={`${asset_resource.label} inline animation preview`}
        autoPlay
        className={VIDEO_ASSET_PREVIEW_MEDIA_CLASS_NAME}
        controls={false}
        loop
        muted
        onCanPlay={video_source.handle_source_load}
        onError={video_source.handle_source_error}
        onLoadedMetadata={video_source.handle_source_load}
        playsInline
        poster={
          should_load_resource
            ? video_asset_playback_source_urls.poster_source_url
            : undefined
        }
        preload={should_load_resource ? "metadata" : "none"}
        src={should_load_resource ? video_source.source_url : undefined}
      />
    </div>
  );
}

function use_should_use_mobile_video_variant() {
  const [should_use_mobile_video_variant, set_should_use_mobile_video_variant] =
    useState(get_should_use_mobile_video_variant);

  useEffect(() => {
    if (typeof window === "undefined" || !("matchMedia" in window)) {
      return;
    }

    const media_query_list = window.matchMedia(MOBILE_VIDEO_ASSET_MEDIA_QUERY);
    const handle_media_query_change = (event: MediaQueryListEvent) => {
      set_should_use_mobile_video_variant(event.matches);
    };

    set_should_use_mobile_video_variant(media_query_list.matches);
    media_query_list.addEventListener("change", handle_media_query_change);

    return () => {
      media_query_list.removeEventListener("change", handle_media_query_change);
    };
  }, []);

  return should_use_mobile_video_variant;
}

function get_should_use_mobile_video_variant() {
  if (typeof window === "undefined" || !("matchMedia" in window)) {
    return false;
  }

  return window.matchMedia(MOBILE_VIDEO_ASSET_MEDIA_QUERY).matches;
}

function ImageAssetPreview({
  asset_resource,
  should_load_resource,
  should_use_original_image_mode,
}: ImageAssetPreviewProps) {
  const [is_final_image_visible, set_is_final_image_visible] = useState(false);
  const image_asset_source_loading_plan = get_image_asset_source_loading_plan({
    should_load_resource,
    should_use_original_image_mode,
  });
  const preview_image_source = use_fallback_resource_source({
    fallback_source_url: asset_resource.preview_fallback_source_url,
    primary_source_url: asset_resource.preview_source_url,
    should_load_source:
      image_asset_source_loading_plan.should_load_preview_source,
    timeout_ms: IMAGE_FALLBACK_TIMEOUT_MS,
  });
  const final_image_source = use_fallback_resource_source({
    fallback_source_url: asset_resource.fallback_source_url,
    primary_source_url: asset_resource.source_url,
    should_load_source: image_asset_source_loading_plan.should_load_final_source,
    timeout_ms: IMAGE_FALLBACK_TIMEOUT_MS,
  });

  const should_render_image_asset =
    image_asset_source_loading_plan.should_render_preview_image ||
    image_asset_source_loading_plan.should_render_final_image;

  const handle_preview_image_error = () => {
    preview_image_source.handle_source_error();
  };
  const handle_preview_image_load = () => {
    preview_image_source.handle_source_load();
  };
  const handle_final_image_load = () => {
    final_image_source.handle_source_load();
    set_is_final_image_visible(true);
  };

  if (!should_render_image_asset) {
    return null;
  }

  return (
    <>
      {image_asset_source_loading_plan.should_render_preview_image ? (
        <img
          aria-hidden="true"
          alt=""
          className={ASSET_PREVIEW_LAYER_CLASS_NAME}
          decoding="async"
          loading="eager"
          onError={handle_preview_image_error}
          onLoad={handle_preview_image_load}
          src={
            image_asset_source_loading_plan.should_load_preview_source
              ? preview_image_source.source_url
              : undefined
          }
        />
      ) : null}
      {image_asset_source_loading_plan.should_render_final_image ? (
        <img
          alt={`${asset_resource.label} image`}
          className={cn(
            FINAL_IMAGE_CLASS_NAME,
            is_final_image_visible && "opacity-100",
          )}
          decoding="async"
          loading="eager"
          onError={final_image_source.handle_source_error}
          onLoad={handle_final_image_load}
          src={
            image_asset_source_loading_plan.should_load_final_source
              ? final_image_source.source_url
              : undefined
          }
        />
      ) : null}
    </>
  );
}

function use_lazy_resource_loading({
  load_trigger_delay_ms,
  root_margin,
  should_load_immediately,
}: UseLazyResourceLoadingParams) {
  const resource_container_ref = useRef<HTMLDivElement>(null);
  const [should_load_resource_source, set_should_load_resource_source] =
    useState(should_load_immediately);

  useEffect(() => {
    if (should_load_resource_source) {
      return;
    }

    const resource_container = resource_container_ref.current;
    let load_resource_timeout_id: number | null = null;

    if (!resource_container || !("IntersectionObserver" in window)) {
      set_should_load_resource_source(true);
      return;
    }

    const clear_pending_resource_load = () => {
      if (!load_resource_timeout_id) {
        return;
      }

      window.clearTimeout(load_resource_timeout_id);
      load_resource_timeout_id = null;
    };

    const load_resource_source = () => {
      clear_pending_resource_load();
      set_should_load_resource_source(true);
      resource_intersection_observer.disconnect();
    };

    const schedule_resource_source_load = () => {
      if (load_resource_timeout_id) {
        return;
      }

      if (load_trigger_delay_ms <= 0) {
        load_resource_source();
        return;
      }

      load_resource_timeout_id = window.setTimeout(
        load_resource_source,
        load_trigger_delay_ms,
      );
    };

    const resource_intersection_observer = new IntersectionObserver(
      (intersection_entries) => {
        const is_resource_near_viewport = intersection_entries.some(
          (intersection_entry) => intersection_entry.isIntersecting,
        );

        if (!is_resource_near_viewport) {
          clear_pending_resource_load();
          return;
        }

        schedule_resource_source_load();
      },
      { rootMargin: root_margin },
    );

    resource_intersection_observer.observe(resource_container);

    return () => {
      clear_pending_resource_load();
      resource_intersection_observer.disconnect();
    };
  }, [load_trigger_delay_ms, root_margin, should_load_resource_source]);

  return {
    resource_container_ref,
    should_load_resource: should_load_resource_source,
  };
}

export function get_asset_preview_container_class_name() {
  return ASSET_PREVIEW_CONTAINER_CLASS_NAME;
}

export function get_image_asset_source_loading_plan({
  should_load_resource,
  should_use_original_image_mode,
}: {
  should_load_resource: boolean;
  should_use_original_image_mode: boolean;
}) {
  const should_load_preview_source = !should_use_original_image_mode;
  const should_load_final_source = should_load_resource;

  return {
    should_load_final_source,
    should_load_preview_source,
    should_render_final_image: should_load_final_source,
    should_render_preview_image: should_load_preview_source,
  };
}

export function get_video_asset_playback_source_urls({
  asset_resource,
  should_use_mobile_video_variant,
}: {
  asset_resource: VideoAssetResource;
  should_use_mobile_video_variant: boolean;
}): VideoAssetVariantSourceUrls {
  if (!asset_resource.responsive_variant_source_urls) {
    return {
      fallback_source_url: asset_resource.fallback_source_url,
      poster_fallback_source_url: asset_resource.poster_fallback_source_url,
      poster_source_url: asset_resource.poster_source_url,
      source_url: asset_resource.source_url,
    };
  }

  return should_use_mobile_video_variant
    ? asset_resource.responsive_variant_source_urls.mobile
    : asset_resource.responsive_variant_source_urls.desktop;
}

export function get_resource_lazy_loading_options(
  asset_resource: Pick<AssetResource, "kind">,
) {
  if (asset_resource.kind === "video") {
    return {
      load_trigger_delay_ms: VIDEO_LOAD_SETTLE_DELAY_MS,
      root_margin: VIDEO_PRELOAD_ROOT_MARGIN,
    };
  }

  return {
    load_trigger_delay_ms: IMAGE_LOAD_SETTLE_DELAY_MS,
    root_margin: IMAGE_PRELOAD_ROOT_MARGIN,
  };
}
